import React, { useEffect, useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import UserProfileCard from '../components/Dashboard/UserProfileCard';
import ClaimWidget from '../components/Dashboard/ClaimWidget';
import StatsCards from '../components/Dashboard/StatsCards';
import ROIChart from '../components/Dashboard/ROIChart';
import ActivityFeed from '../components/Dashboard/ActivityFeed';
import PageLoader from '../components/Loader/PageLoader';
import Card from '../components/Common/Card';
import Button from '../components/Common/Button';
import { useNavigate } from 'react-router-dom';
import { getRankFromEligibility } from '../config/contract';

const EMPTY_STATS = {
  totalInvestment: '0',
  activeInvestment: '0',
  totalEarned: '0',
  pendingRewards: '0',
  directReferrals: '0',
  teamVolume: '0'
};

const Dashboard = () => {
  const { contract, account, userId, onCorrectNetwork } = useWeb3();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [pendingROI, setPendingROI] = useState('0');
  const [activities, setActivities] = useState([]);
  const [referrerId, setReferrerId] = useState('0');
  const [walletAddr, setWalletAddr] = useState(null);
  const [active, setActive] = useState(false);
  const [isBlacklisted, setIsBlacklisted] = useState(false);
  const [claimFee, setClaimFee] = useState('1000');
  const [dailyLimit, setDailyLimit] = useState('0');
  const [remaining, setRemaining] = useState('0');
  const [resetsAt, setResetsAt] = useState(0);
  const [rank, setRank] = useState({ level: 0, name: 'GUEST', color: '#6b7a92' });
  const [reloadKey, setReloadKey] = useState(0);

  const load = async () => {
    if (!contract || !account || !userId) {
      setLoading(false);
      return;
    }
    try {
      const [basic, ext, currentROI, acts, fees, limit, remInfo, elig] = await Promise.all([
        contract.getUserBasicInfo(userId),
        contract.getUserExtendedInfo(userId),
        contract.calculateCurrentROI(userId),
        contract.getUserActivities(userId, 0, 50),
        contract.getFees(),
        contract.dailyWithdrawLimit(),
        contract.getRemainingDailyLimit(userId),
        contract.getUserLevelEligibility(userId)
      ]);
      setWalletAddr(basic.wallet);
      setStats({
        totalInvestment: basic.totalInvestment.toString(),
        activeInvestment: basic.activeInvestment.toString(),
        totalEarned: basic.totalEarned.toString(),
        pendingRewards: basic.pendingRewards.toString(),
        directReferrals: ext.directReferrals.toString(),
        teamVolume: ext.teamVolume.toString()
      });
      setPendingROI(currentROI.toString());
      setActivities(acts[0] || []);
      setReferrerId(ext.referrerId.toString());
      setActive(ext.active);
      setIsBlacklisted(ext.isBlacklisted);
      setClaimFee(fees._claimFee?.toString() || fees[2]?.toString() || '1000');
      setDailyLimit(limit.toString());
      setRemaining(remInfo.remaining.toString());
      setResetsAt(Number(remInfo.windowResetsAt.toString()));
      setRank(getRankFromEligibility(elig));
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [contract, account, userId, onCorrectNetwork, reloadKey]);

  const refresh = () => setReloadKey((k) => k + 1);

  if (!account) {
    return (
      <Card>
        <div className="empty-state">
          <div className="empty-state-icon">◌</div>
          <div className="empty-state-text">Connect your wallet to continue</div>
        </div>
      </Card>
    );
  }

  if (loading) return <PageLoader text="Loading Dashboard" />;

  if (!userId) {
    return (
      <Card title="Get Started" subtitle="Register to begin earning">
        <div className="empty-state">
          <div className="empty-state-icon">↑</div>
          <div className="empty-state-text" style={{ marginBottom: 24 }}>
            You haven't registered yet
          </div>
          <Button variant="primary" size="lg" onClick={() => navigate('/invest')}>
            Register & Stake
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <h1 className="page-title">Dashboard</h1>
      <p className="page-subtitle">Welcome back · Track everything from here</p>

      <UserProfileCard
        userId={userId}
        referrerId={referrerId}
        wallet={walletAddr || account}
        active={active}
        isBlacklisted={isBlacklisted}
        rank={rank}
      />

      <ClaimWidget
        pendingRewards={stats.pendingRewards}
        pendingROI={pendingROI}
        claimFee={claimFee}
        dailyLimit={dailyLimit}
        remaining={remaining}
        resetsAt={resetsAt}
        userId={userId}
        onChange={refresh}
      />

      <StatsCards stats={stats} />

      <div className="dashboard-grid">
        <ROIChart stats={stats} pendingROI={pendingROI} />
        <ActivityFeed activities={activities} />
      </div>
    </div>
  );
};

export default Dashboard;
